import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Loader2, Download, Send, FileText, Calendar, Copy, RefreshCw, RefreshCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { login, logout, isAuthed, adminApi, clearToken, getToken } from "../lib/adminAuth";
import { GlossyCross } from "../components/GlossyCross";
import { Reveal } from "../components/Reveal";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(isAuthed());
  const [email, setEmail] = useState("admin@swissalpenline.ch");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, pass);
      setAuthed(true);
      toast.success("Signed in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      clearToken();
    } finally { setLoading(false); }
  };

  if (!authed) {
    return (
      <div className="pt-32 pb-24 px-6 min-h-[80vh] flex items-center">
        <div className="max-w-md mx-auto w-full">
          <div className="text-center mb-10">
            <GlossyCross size={40} className="mx-auto mb-4" />
            <p className="wordmark text-3xl">Swiss AlpenLine</p>
            <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mt-2">Admin console</p>
          </div>
          <form onSubmit={doLogin} className="border border-brand-border p-8 space-y-5">
            <label className="block">
              <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">Email</span>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="mt-2 w-full bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
              />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">Password</span>
              <input
                type="password" value={pass} onChange={(e) => setPass(e.target.value)}
                data-testid="admin-password"
                className="mt-2 w-full bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
              />
            </label>
            <button type="submit" disabled={loading} data-testid="admin-login-btn"
                    className="btn-pill btn-pill--solid w-full justify-center">
              {loading ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : <><Lock size={14} /> Sign in</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <Dashboard onLogout={() => { logout(); setAuthed(false); navigate("/admin"); }} />;
};

const TABS = ["reservations", "invoices", "calendar", "email", "pricing", "rules", "partners", "sync"];

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("reservations");
  return (
    <div className="pt-28 pb-24 px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between border-b border-brand-border pb-6 mb-10">
          <div>
            <p className="text-xs tracking-widest2 uppercase text-brand-silver">— Admin console</p>
            <h1 className="text-3xl font-light text-brand-platinum mt-1">Dashboard</h1>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="btn-pill">
            <LogOut size={14} /> Sign out
          </button>
        </div>
        <div className="flex gap-6 mb-8 flex-wrap">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`admin-tab-${t}`}
                    className={`text-xs tracking-widest2 uppercase pb-2 border-b transition-colors ${
                      tab === t ? "text-brand-platinum border-brand-platinum" : "text-brand-silver border-transparent hover:text-brand-platinum"
                    }`}>
              {t}
            </button>
          ))}
        </div>
        {tab === "reservations" && <ReservationsPanel />}
        {tab === "invoices" && <InvoicesPanel />}
        {tab === "calendar" && <CalendarPanel />}
        {tab === "email" && <ResendPanel />}
        {tab === "pricing" && <PricingPanel />}
        {tab === "rules" && <BusinessRulesPanel />}
        {tab === "partners" && <PartnerReportsPanel />}
        {tab === "sync" && <CalendarSyncPanel />}
      </div>
    </div>
  );
};

const ReservationsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/reservations");
      setRows(data);
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const complete = async (id) => {
    try {
      await adminApi.post(`/admin/reservations/${id}/complete`);
      toast.success("Marked as completed — funds released");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const advanceStatus = async (id, next) => {
    try {
      await adminApi.put(`/admin/reservations/${id}/chauffeur-status`,
        { chauffeur_status: next });
      toast.success(`Chauffeur status → ${next.replace(/_/g, " ")}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const trackingUrl = (id) => {
    const base = window.location.origin;
    return `${base}/reserva/status/${id}`;
  };

  const copyTrackingUrl = async (id) => {
    try {
      await navigator.clipboard.writeText(trackingUrl(id));
      toast.success("Tracking URL copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const CHAUFFEUR_STAGES = [
    "confirmed", "en_route_to_pickup", "arrived_at_pickup",
    "passenger_on_board", "completed",
  ];

  if (loading) return <p className="text-brand-silver text-sm">Loading…</p>;
  if (rows.length === 0) return <p className="text-brand-silver text-sm">No reservations yet.</p>;

  return (
    <div className="border border-brand-border overflow-x-auto" data-testid="admin-reservations-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-brand-border text-[10px] tracking-widest2 uppercase text-brand-silver">
            <th className="text-left py-3 px-4 font-normal">Ref</th>
            <th className="text-left py-3 px-4 font-normal">Contact</th>
            <th className="text-left py-3 px-4 font-normal">Route</th>
            <th className="text-left py-3 px-4 font-normal">Pickup</th>
            <th className="text-right py-3 px-4 font-normal">CHF</th>
            <th className="text-left py-3 px-4 font-normal">Status</th>
            <th className="text-right py-3 px-4 font-normal">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const stage = r.chauffeur_status || "confirmed";
            const idx = CHAUFFEUR_STAGES.indexOf(stage);
            const nextStage = idx >= 0 && idx < CHAUFFEUR_STAGES.length - 1 ? CHAUFFEUR_STAGES[idx + 1] : null;
            return (
            <tr key={r.id} className="border-b border-brand-border text-brand-platinum">
              <td className="py-3 px-4 font-mono text-xs">{r.id.slice(0, 8).toUpperCase()}</td>
              <td className="py-3 px-4">
                <div>{r.contact_name}</div>
                <div className="text-xs text-brand-silver">{r.contact_email}</div>
              </td>
              <td className="py-3 px-4 text-xs">{(r.origin || "—")} → {(r.destination || "—")}</td>
              <td className="py-3 px-4 text-xs">{new Date(r.pickup_datetime).toLocaleString()}</td>
              <td className="py-3 px-4 text-right tabular-nums">{(r.total_chf || 0).toFixed(2)}</td>
              <td className="py-3 px-4 text-xs">
                <div className="flex flex-col gap-1">
                  <span className={`inline-block px-2 py-1 border ${
                    r.status === "service_completed" ? "border-brand-platinum text-brand-platinum" : "border-brand-border text-brand-silver"
                  }`}>{r.status}</span>
                  <span className="text-[10px] text-bronze tabular-nums" data-testid={`chauffeur-stage-${r.id}`}>
                    {stage.replace(/_/g, " ")}
                  </span>
                </div>
              </td>
              <td className="py-3 px-4 text-right space-y-1">
                {nextStage && r.payment_status === "paid" && (
                  <button onClick={() => advanceStatus(r.id, nextStage)}
                          data-testid={`advance-${r.id}`}
                          className="block ml-auto text-[10px] tracking-widest2 uppercase text-bronze link-underline">
                    Advance → {nextStage.replace(/_/g, " ")}
                  </button>
                )}
                <button onClick={() => copyTrackingUrl(r.id)}
                        data-testid={`copy-track-${r.id}`}
                        className="block ml-auto text-[10px] tracking-widest2 uppercase text-brand-silver link-underline">
                  Copy track URL
                </button>
                {r.payment_status === "paid" && r.status !== "service_completed" && (
                  <button onClick={() => complete(r.id)} className="block ml-auto text-[10px] tracking-widest2 uppercase text-brand-platinum link-underline">
                    Mark completed
                  </button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const InvoicesPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(""); // YYYY-MM or empty

  const refresh = async () => {
    setLoading(true);
    try {
      const params = month ? { params: { month } } : {};
      const { data } = await adminApi.get("/admin/invoices", params);
      setRows(data);
    } catch (e) { toast.error("Failed to load invoices"); }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, [month]);

  const downloadPdf = async (invId, number) => {
    try {
      const resp = await adminApi.get(`/admin/invoices/${invId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF download failed"); }
  };

  const exportCsv = async () => {
    try {
      const params = month ? { params: { month } } : {};
      const resp = await adminApi.get("/admin/invoices/export", { ...params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `magic-heidi-${month || "all"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) { toast.error("CSV export failed"); }
  };

  const resend = async (invId) => {
    try {
      await adminApi.post(`/admin/invoices/${invId}/regenerate`);
      toast.success("Invoice re-sent to customer");
    } catch (e) { toast.error("Failed to re-send"); }
  };

  return (
    <div data-testid="admin-invoices-panel">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="text-[10px] tracking-widest2 uppercase text-brand-silver">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="invoices-month-filter"
            className="bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm"
          />
          {month && (
            <button onClick={() => setMonth("")} className="text-xs text-brand-silver hover:text-brand-platinum">Clear</button>
          )}
        </div>
        <button onClick={exportCsv} data-testid="invoices-export-csv" className="btn-pill">
          <Download size={14} /> Export CSV (Magic Heidi)
        </button>
      </div>

      {loading ? (
        <p className="text-brand-silver text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-brand-silver text-sm">No invoices yet.</p>
      ) : (
        <div className="border border-brand-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-[10px] tracking-widest2 uppercase text-brand-silver">
                <th className="text-left py-3 px-4 font-normal">Number</th>
                <th className="text-left py-3 px-4 font-normal">Customer</th>
                <th className="text-left py-3 px-4 font-normal">Trip</th>
                <th className="text-left py-3 px-4 font-normal">Service</th>
                <th className="text-right py-3 px-4 font-normal">CHF</th>
                <th className="text-left py-3 px-4 font-normal">Status</th>
                <th className="text-right py-3 px-4 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.number} className="border-b border-brand-border text-brand-platinum">
                  <td className="py-3 px-4 font-mono text-xs">{r.number}</td>
                  <td className="py-3 px-4">
                    <div>{r.customer_name}</div>
                    <div className="text-xs text-brand-silver">{r.customer_email}</div>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {r.trip_date ? new Date(r.trip_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 px-4 text-xs capitalize">{r.service}{r.hour_tier && r.hour_tier !== "normal" ? ` · ${r.hour_tier}` : ""}</td>
                  <td className="py-3 px-4 text-right tabular-nums">{r.amount_chf.toFixed(2)}</td>
                  <td className="py-3 px-4 text-xs">
                    <span className={`inline-block px-2 py-1 border ${
                      r.status === "paid_confirmed" ? "border-brand-platinum text-brand-platinum" : "border-brand-border text-brand-silver"
                    }`}>{r.status === "paid_confirmed" ? "confirmed" : "pending"}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-3">
                      <button onClick={() => downloadPdf(r.id, r.number)} title="Download PDF"
                              data-testid={`invoice-pdf-${r.number}`}
                              className="text-brand-silver hover:text-brand-platinum">
                        <FileText size={14} />
                      </button>
                      <button onClick={() => resend(r.id)} title="Re-send email"
                              data-testid={`invoice-resend-${r.number}`}
                              className="text-brand-silver hover:text-brand-platinum">
                        <Send size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CalendarPanel = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/calendar-token");
      setInfo(data);
    } catch (e) { toast.error("Failed to load calendar token"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (!window.confirm("Regenerate URL? Existing calendar subscriptions will STOP working — you'll need to re-subscribe with the new URL.")) return;
    setRegenerating(true);
    try {
      const { data } = await adminApi.post("/admin/calendar-token/regenerate");
      setInfo(data);
      toast.success("New URL generated. Update your calendar subscriptions.");
    } catch (e) { toast.error("Failed to regenerate"); }
    setRegenerating(false);
  };

  const url = info ? `${API_BASE}${info.path}` : "";
  const webcal = url.replace(/^https?/, "webcal");

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (loading) return <p className="text-brand-silver text-sm">Loading…</p>;

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-calendar-panel">
      <div>
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">— Subscription URL</p>
        <h2 className="text-2xl font-light text-brand-platinum mb-4">Sync every booking to your calendar</h2>
        <p className="text-sm text-brand-silver leading-relaxed max-w-2xl">
          Subscribe your Google, Apple or Outlook calendar to the URL below and every confirmed booking will
          appear automatically. Cancellations remove themselves. Refresh interval: ~30 min (Google/Outlook
          may take up to 24 h to refresh — Apple every 15 min).
        </p>
      </div>

      <div className="border border-brand-border p-6 space-y-4">
        <label className="block">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">HTTPS URL (Google Calendar / Outlook)</span>
          <div className="mt-2 flex gap-2">
            <input readOnly value={url} data-testid="calendar-url-https"
                   className="flex-1 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-xs font-mono select-all"
                   onClick={(e) => e.target.select()} />
            <button onClick={() => copy(url, "URL")} data-testid="calendar-copy-https"
                    className="btn-pill"><Copy size={12} /></button>
          </div>
        </label>

        <label className="block">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">webcal:// URL (Apple Calendar — one-click)</span>
          <div className="mt-2 flex gap-2">
            <input readOnly value={webcal} data-testid="calendar-url-webcal"
                   className="flex-1 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-xs font-mono select-all"
                   onClick={(e) => e.target.select()} />
            <a href={webcal} data-testid="calendar-webcal-link" className="btn-pill">
              <Calendar size={12} /> Open
            </a>
          </div>
        </label>

        <div className="pt-4 border-t border-brand-border flex justify-end">
          <button onClick={regenerate} disabled={regenerating} data-testid="calendar-regenerate"
                  className="btn-pill">
            {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Regenerate URL
          </button>
        </div>
      </div>

      <div className="border border-brand-border p-6 space-y-5 text-sm">
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">— How to subscribe</p>

        <div>
          <p className="text-brand-platinum mb-2">Google Calendar</p>
          <ol className="text-brand-silver text-xs leading-relaxed list-decimal pl-4 space-y-1">
            <li>Open <a href="https://calendar.google.com/calendar/r/settings/addbyurl" target="_blank" rel="noreferrer" className="text-brand-platinum underline">calendar.google.com → Add calendar → From URL</a></li>
            <li>Paste the HTTPS URL above and click <em>Add calendar</em>.</li>
            <li>Google refreshes every 12–24 h (this is a Google limitation).</li>
          </ol>
        </div>

        <div>
          <p className="text-brand-platinum mb-2">Apple Calendar (iPhone / Mac)</p>
          <ol className="text-brand-silver text-xs leading-relaxed list-decimal pl-4 space-y-1">
            <li>On iPhone: click the <em>webcal://</em> URL above → tap <em>Subscribe</em>.</li>
            <li>On Mac Calendar: File → New Calendar Subscription → paste the webcal URL.</li>
            <li>Set refresh to <em>Every 15 minutes</em>.</li>
          </ol>
        </div>

        <div>
          <p className="text-brand-platinum mb-2">Microsoft Outlook</p>
          <ol className="text-brand-silver text-xs leading-relaxed list-decimal pl-4 space-y-1">
            <li>Outlook (web): <em>Add calendar → Subscribe from web</em> → paste HTTPS URL.</li>
            <li>Outlook desktop: <em>Add calendar → From Internet</em> → paste HTTPS URL.</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

const ResendPanel = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/resend/domain");
      setInfo(data);
    } catch (e) { toast.error("Failed to load Resend status"); }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await adminApi.post("/admin/resend/domain/verify");
      setInfo(data);
      toast.success("Re-verification triggered.");
    } catch (e) { toast.error("Re-verify failed"); }
    setVerifying(false);
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  if (loading) return <p className="text-brand-silver text-sm">Loading…</p>;
  if (!info || info.error) return <p className="text-brand-silver text-sm">Resend not configured or unreachable.</p>;

  const st = info.status;
  const badge = st === "verified"
    ? { txt: "VERIFIED", color: "#7fbf7f" }
    : st === "pending"
      ? { txt: "PENDING", color: "#B08D2F" }
      : { txt: st.toUpperCase(), color: "#c96b6b" };

  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-resend-panel">
      <div>
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">— Email sender domain</p>
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-2xl font-light text-brand-platinum">{info.name}</h2>
          <span className="px-3 py-1 border text-[10px] tracking-widest2 uppercase" style={{ color: badge.color, borderColor: badge.color }} data-testid="resend-status-badge">
            {badge.txt}
          </span>
          <span className="text-xs text-brand-silver">Region: {info.region}</span>
          <button onClick={verify} disabled={verifying || st === "verified"} data-testid="resend-verify-btn"
                  className="btn-pill ml-auto disabled:opacity-40">
            {verifying ? "Verifying…" : "Re-verify now"}
          </button>
        </div>
        <p className="text-brand-silver text-sm mt-4 max-w-2xl leading-relaxed">
          {st === "verified"
            ? "Your domain is verified. Outbound emails from info@swissalpenline.ch are being delivered without spoofing warnings."
            : "Add the three DNS records below to your Hostpoint DNS editor, then click Re-verify. Status is refreshed automatically every 30 s."}
        </p>
      </div>

      <div>
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">— Required DNS records</p>
        <div className="border border-brand-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-brand-silver border-b border-brand-border">
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Host / Name</th>
                <th className="text-left px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">Priority</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {info.records?.map((r, i) => (
                <tr key={i} className="border-b border-brand-border align-top">
                  <td className="px-3 py-3 text-brand-platinum">{r.record}</td>
                  <td className="px-3 py-3 text-brand-platinum">{r.type}</td>
                  <td className="px-3 py-3 text-brand-silver font-mono">{r.name}.{info.name}</td>
                  <td className="px-3 py-3 text-brand-silver font-mono break-all max-w-[420px]">{r.value}</td>
                  <td className="px-3 py-3 text-brand-silver">{r.priority ?? "—"}</td>
                  <td className="px-3 py-3">
                    <span className="text-[10px] uppercase tracking-widest2"
                          style={{ color: r.status === "verified" ? "#7fbf7f" : r.status === "pending" ? "#B08D2F" : "#c96b6b" }}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => copy(r.value, r.record + " value")} className="text-[10px] tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum">
                      copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-brand-border p-5 bg-brand-surface">
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">— Quick instructions</p>
        <ol className="text-sm text-brand-silver leading-relaxed list-decimal pl-5 space-y-1">
          <li>Log in to <span className="text-brand-platinum">admin.hostpoint.ch</span> → Services → Domains → swissalpenline.ch → DNS Editor.</li>
          <li>Add the 3 records above <strong>exactly</strong> as shown (copy button to the right).</li>
          <li>Save and wait 5-30 min for propagation.</li>
          <li>Click <strong>Re-verify now</strong>. Status here updates automatically every 30 s.</li>
        </ol>
      </div>
    </div>
  );
};

const PricingPanel = () => {
  const [p, setP] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get("/pricing").then((r) => setP(r.data)); }, []);

  const save = async () => {
    if (Object.keys(edits).length === 0) return;
    setSaving(true);
    try {
      const num = Object.fromEntries(Object.entries(edits).map(([k, v]) => [k, Number(v)]));
      const { data } = await adminApi.put("/admin/pricing", { updates: num });
      setP(data.pricing);
      setEdits({});
      toast.success("Pricing updated");
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (!p) return <p className="text-brand-silver text-sm">Loading…</p>;

  const groups = [
    { title: "Transfer", keys: ["base_fare_chf", "rate_per_km", "degression_km", "degression_rate_per_km"] },
    { title: "Surcharges", keys: ["intermediate_supplement_pct", "night_supplement_pct", "weekend_supplement_pct"] },
    { title: "Full day", keys: ["full_day_base_chf", "full_day_included_hours", "full_day_extra_hour_chf"] },
    { title: "By hour", keys: ["by_hour_rate_chf", "by_hour_min_hours", "by_hour_included_km_per_hour", "by_hour_extra_km_chf"] },
    { title: "Courier", keys: ["courier_base_chf", "courier_rate_per_km", "courier_night_supplement_pct", "courier_weekend_supplement_pct"] },
    { title: "Extras", keys: ["child_seat_chf", "pet_no_carrier_chf", "pet_with_carrier_chf"] },
    { title: "Engine · vehicle /km", keys: ["depreciation_per_km_chf", "wear_per_km_chf", "tyres_per_km_chf", "brakes_per_km_chf", "maintenance_per_km_chf", "energy_per_km_chf"] },
    { title: "Engine · hourly & trip", keys: ["insurance_per_hour_chf", "driver_labour_per_hour_chf", "cleaning_per_trip_chf", "airport_fee_chf", "parking_per_trip_chf", "tolls_per_km_chf"] },
    { title: "Engine · platform", keys: ["software_per_trip_chf", "google_api_per_trip_chf", "business_ops_per_trip_chf", "stripe_pct", "stripe_fixed_chf", "pass_stripe_to_customer"] },
    { title: "Engine · margin & floor", keys: ["empty_km_ratio", "repositioning_min", "company_margin_pct", "quote_floor_chf"] },
    { title: "Benchmarks", keys: ["taxi_base_chf", "taxi_rate_per_km", "uber_black_base_chf", "uber_black_rate_per_km", "other_chauffeur_base_chf", "other_chauffeur_rate_per_km", "train_rate_per_km"] },
    { title: "Currency", keys: ["usd_to_chf", "eur_to_chf", "identity_verify_threshold_chf"] },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-pricing-panel">
        {groups.map((g) => (
          <div key={g.title} className="border border-brand-border p-5">
            <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">{g.title}</p>
            <div className="space-y-3">
              {g.keys.map((k) => (
                <label key={k} className="flex items-center justify-between gap-4">
                  <span className="text-xs text-brand-silver">{k}</span>
                  <input
                    type="number" step="0.01"
                    defaultValue={p[k]}
                    onChange={(e) => setEdits((prev) => ({ ...prev, [k]: e.target.value }))}
                    data-testid={`pricing-${k}`}
                    className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right"
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex justify-end">
        <button onClick={save} disabled={saving || Object.keys(edits).length === 0}
                data-testid="pricing-save"
                className="btn-pill btn-pill--solid">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default Admin;

// ---------------------------------------------------------------------------
// Business Rules panel — Fase C: configurable business hours, minimum
// after-hours booking value, wait-time policy, closure days, and winter
// altitude safety restrictions. Persisted to Mongo via /api/admin/business-rules.
// ---------------------------------------------------------------------------

const BusinessRulesPanel = () => {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newClosureDate, setNewClosureDate] = useState("");

  useEffect(() => {
    adminApi.get("/business-rules").then((res) => setR(res.data));
  }, []);

  const patch = (updates) => setR((prev) => ({ ...prev, ...updates }));

  const toggleMonth = (m) => {
    setR((prev) => {
      const cur = new Set(prev.winter_months || []);
      if (cur.has(m)) cur.delete(m); else cur.add(m);
      return { ...prev, winter_months: Array.from(cur).sort((a, b) => a - b) };
    });
  };

  const addClosure = () => {
    if (!newClosureDate) return;
    setR((prev) => ({
      ...prev,
      closure_dates: Array.from(new Set([...(prev.closure_dates || []), newClosureDate])).sort(),
    }));
    setNewClosureDate("");
  };

  const removeClosure = (d) =>
    setR((prev) => ({ ...prev, closure_dates: (prev.closure_dates || []).filter((x) => x !== d) }));

  const save = async () => {
    setSaving(true);
    try {
      const updates = {
        business_hours_start: r.business_hours_start,
        business_hours_end: r.business_hours_end,
        after_hours_min_chf: Number(r.after_hours_min_chf) || 0,
        wait_time_grace_min: Number(r.wait_time_grace_min) || 0,
        wait_time_rate_chf_per_15min: Number(r.wait_time_rate_chf_per_15min) || 0,
        closure_dates: r.closure_dates || [],
        altitude_restrictions_enabled: !!r.altitude_restrictions_enabled,
        altitude_max_m: Number(r.altitude_max_m) || 0,
        winter_months: r.winter_months || [],
        meteoswiss_url: r.meteoswiss_url || "",
      };
      const { data } = await adminApi.put("/admin/business-rules", { updates });
      setR(data.business_rules);
      toast.success("Business rules updated");
    } catch (e) {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  if (!r) return <p className="text-brand-silver text-sm">Loading…</p>;

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div data-testid="admin-rules-panel" className="space-y-6">
      <p className="text-sm text-brand-silver max-w-3xl">
        Configurable dispatch policy. These values are enforced by the quote and
        reservation endpoints. Empty or zero disables the corresponding rule.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Business hours */}
        <div className="border border-brand-border p-5">
          <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">Business hours (Europe/Zurich)</p>
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">Start (HH:MM)</span>
              <input type="time" value={r.business_hours_start}
                     onChange={(e) => patch({ business_hours_start: e.target.value })}
                     data-testid="rules-hours-start"
                     className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right" />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">End (HH:MM)</span>
              <input type="time" value={r.business_hours_end}
                     onChange={(e) => patch({ business_hours_end: e.target.value })}
                     data-testid="rules-hours-end"
                     className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right" />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">After-hours min. quote (CHF, 0 = disabled)</span>
              <input type="number" step="1" value={r.after_hours_min_chf}
                     onChange={(e) => patch({ after_hours_min_chf: e.target.value })}
                     data-testid="rules-min-chf"
                     className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right" />
            </label>
          </div>
        </div>

        {/* Wait time */}
        <div className="border border-brand-border p-5">
          <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">Wait-time policy</p>
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">Free wait (min)</span>
              <input type="number" step="1" value={r.wait_time_grace_min}
                     onChange={(e) => patch({ wait_time_grace_min: e.target.value })}
                     data-testid="rules-wait-grace"
                     className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right" />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">Rate per 15 min after grace (CHF)</span>
              <input type="number" step="1" value={r.wait_time_rate_chf_per_15min}
                     onChange={(e) => patch({ wait_time_rate_chf_per_15min: e.target.value })}
                     data-testid="rules-wait-rate"
                     className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right" />
            </label>
          </div>
        </div>

        {/* Winter altitude */}
        <div className="border border-brand-border p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">Winter altitude safety</p>
            <label className="flex items-center gap-2 text-xs text-brand-silver">
              <input type="checkbox" checked={!!r.altitude_restrictions_enabled}
                     onChange={(e) => patch({ altitude_restrictions_enabled: e.target.checked })}
                     data-testid="rules-altitude-enabled" />
              Enabled
            </label>
          </div>
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">Max altitude in winter (m)</span>
              <input type="number" step="50" value={r.altitude_max_m}
                     onChange={(e) => patch({ altitude_max_m: e.target.value })}
                     data-testid="rules-altitude-max"
                     className="w-32 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm text-right" />
            </label>
            <div>
              <span className="text-xs text-brand-silver block mb-2">Winter months (click to toggle)</span>
              <div className="grid grid-cols-6 gap-1">
                {MONTHS.map((m, i) => {
                  const active = (r.winter_months || []).includes(i + 1);
                  return (
                    <button key={m} type="button" onClick={() => toggleMonth(i + 1)}
                            data-testid={`rules-month-${i + 1}`}
                            className={`text-[10px] tracking-widest2 uppercase py-1 border transition-colors ${
                              active
                                ? "border-brand-platinum text-brand-platinum bg-brand-surface"
                                : "border-brand-border text-brand-silver hover:text-brand-platinum"
                            }`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center justify-between gap-4">
              <span className="text-xs text-brand-silver">MeteoSwiss URL</span>
              <input type="url" value={r.meteoswiss_url || ""}
                     onChange={(e) => patch({ meteoswiss_url: e.target.value })}
                     data-testid="rules-meteoswiss-url"
                     className="w-56 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-xs text-right" />
            </label>
          </div>
        </div>

        {/* Closure days */}
        <div className="border border-brand-border p-5">
          <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">Closure days (full-day unavailability)</p>
          <div className="flex items-center gap-2 mb-3">
            <input type="date" value={newClosureDate}
                   onChange={(e) => setNewClosureDate(e.target.value)}
                   data-testid="rules-closure-add-input"
                   className="flex-1 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm" />
            <button type="button" onClick={addClosure} data-testid="rules-closure-add-btn"
                    className="btn-pill text-xs">
              Add
            </button>
          </div>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {(r.closure_dates || []).length === 0 && (
              <li className="text-xs text-brand-dim italic">No closure days configured.</li>
            )}
            {(r.closure_dates || []).map((d) => (
              <li key={d} className="flex items-center justify-between text-sm text-brand-silver">
                <span className="tabular-nums">{d}</span>
                <button type="button" onClick={() => removeClosure(d)}
                        data-testid={`rules-closure-remove-${d}`}
                        className="text-[10px] tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum">
                  remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
                data-testid="rules-save"
                className="btn-pill btn-pill--solid">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save rules"}
        </button>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Partner Reports panel — P2: consolidated monthly statements for hotel /
// family-office partners. Preview PDF, download CSV, email one-by-one, or
// batch-send to every active partner.
// ---------------------------------------------------------------------------

const currentYm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const PartnerReportsPanel = () => {
  const [partners, setPartners] = useState([]);
  const [month, setMonth] = useState(currentYm());
  const [selected, setSelected] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerRuns, setSchedulerRuns] = useState([]);

  const loadSchedulerData = async () => {
    try {
      const s = await adminApi.get("/admin/scheduler/status");
      setSchedulerStatus(s.data);
      const r = await adminApi.get("/admin/scheduler/runs");
      setSchedulerRuns(r.data.runs || []);
    } catch {
      // scheduler is optional
    }
  };

  useEffect(() => {
    adminApi.get("/admin/partners").then((r) => {
      setPartners(r.data.partners || []);
      if (r.data.partners?.length && !selected) setSelected(r.data.partners[0].code);
    });
    loadSchedulerData();
  }, []);

  const runSchedulerNow = async () => {
    if (!confirm("Manually trigger the monthly-statement job for the previous month? Emails will be sent to every active partner that had paid trips.")) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/scheduler/run-now");
      const s = data.latest_run || {};
      toast.success(`Ran ${s.month} · sent ${s.sent?.length || 0} · skipped ${s.skipped?.length || 0}`);
      await loadSchedulerData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    }
    setBusy(false);
  };

  const toggleAutoReports = async (code, next) => {
    try {
      await adminApi.put(`/admin/partners/${code}/auto-reports`, { disable_auto_reports: next });
      setPartners((prev) => prev.map((p) => p.code === code ? { ...p, disable_auto_reports: next } : p));
      toast.success(next ? `${code} · auto-send disabled` : `${code} · auto-send enabled`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const setDeliveryFormat = async (code, fmt) => {
    try {
      await adminApi.put(`/admin/partners/${code}/delivery-format`, { delivery_format: fmt });
      setPartners((prev) => prev.map((p) => p.code === code ? { ...p, delivery_format: fmt } : p));
      toast.success(`${code} · delivery ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const preview = async () => {
    if (!selected || !month) return;
    setLoading(true); setReport(null);
    try {
      const { data } = await adminApi.get("/admin/partner-reports",
        { params: { month, partner: selected } });
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    }
    setLoading(false);
  };

  const openPdf = () => {
    if (!selected || !month) return;
    const token = getToken();
    const url = `${API_BASE}/api/admin/partner-reports/pdf?month=${month}&partner=${selected}&token=${encodeURIComponent(token)}`;
    // Since the endpoint requires a Bearer token, fetch with auth then open a blob.
    adminApi.get("/admin/partner-reports/pdf",
      { params: { month, partner: selected }, responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
      })
      .catch(() => toast.error("Failed to open PDF"));
  };

  const downloadCsv = async () => {
    try {
      const { data } = await adminApi.get("/admin/partner-reports/csv",
        { params: { month, partner: selected }, responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `partner-${selected}-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  const sendOne = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/partner-reports/send",
        null, { params: { month, partner: selected } });
      toast.success(`Statement emailed to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setBusy(false);
  };

  const sendAll = async () => {
    if (!confirm(`Send monthly statements to ALL active partners for ${month}?`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/partner-reports/send-all",
        null, { params: { month } });
      toast.success(`Sent ${data.sent.length} · skipped ${data.skipped.length}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch send failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="admin-partners-panel" className="space-y-6">
      <p className="text-sm text-brand-silver max-w-3xl">
        Consolidated monthly statements for hotel and family-office partners.
        Preview any month, download the CSV for accounting, or email the PDF
        statement directly to the partner via Resend.
      </p>

      {/* Scheduler status — auto-send on the 1st of every month */}
      {schedulerStatus && (
        <div className="border border-brand-border p-5 space-y-3" data-testid="scheduler-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-1">
                Automatic monthly statements
              </p>
              {schedulerStatus.enabled ? (
                <p className="text-sm text-brand-platinum">
                  Enabled · next automatic run: <span className="tabular-nums">
                  {schedulerStatus.jobs?.[0]?.next_run
                    ? new Date(schedulerStatus.jobs[0].next_run).toLocaleString(undefined, {
                        weekday: "short", day: "2-digit", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : "—"}
                  </span>
                </p>
              ) : (
                <p className="text-sm text-brand-silver italic">
                  Scheduler disabled — set <code className="text-bronze">ENABLE_SCHEDULER=1</code> in backend .env to enable.
                </p>
              )}
            </div>
            {schedulerStatus.enabled && (
              <button onClick={runSchedulerNow} disabled={busy}
                      data-testid="scheduler-run-now"
                      className="btn-pill">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Run now (previous month)
              </button>
            )}
          </div>
          {schedulerRuns.length > 0 && (
            <div className="pt-3 border-t border-brand-border/50">
              <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">Recent runs</p>
              <ul className="space-y-1 text-xs">
                {schedulerRuns.slice(0, 5).map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-brand-silver">
                    <span className="tabular-nums">
                      {new Date(r.run_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {" · "}
                      <span className="text-brand-platinum">{r.month}</span>
                    </span>
                    <span>
                      <span className="text-brand-platinum">{r.sent?.length || 0}</span> sent
                      {" · "}
                      <span className="text-bronze">{r.skipped?.length || 0}</span> skipped
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Opt-out matrix — hotels that self-manage billing can be excluded */}
          {partners.length > 0 && (
            <div className="pt-3 border-t border-brand-border/50">
              <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">
                Auto-send preferences
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                {partners.map((p) => {
                  const off = !!p.disable_auto_reports;
                  const disabled = !p.email;
                  const fmt = (p.delivery_format || "both").toLowerCase();
                  return (
                    <li key={p.code} className="flex items-center justify-between py-1 gap-4">
                      <span className="text-brand-silver truncate max-w-[45%]" title={`${p.name} · ${p.email || "no email"}`}>
                        {p.name}
                      </span>
                      <div className="flex items-center gap-4">
                        <select value={fmt}
                                disabled={disabled || off}
                                onChange={(e) => setDeliveryFormat(p.code, e.target.value)}
                                data-testid={`delivery-fmt-${p.code}`}
                                className={`text-[10px] tracking-widest2 uppercase bg-brand-bg border border-brand-border text-brand-platinum px-2 py-1 ${(disabled || off) ? "opacity-40" : ""}`}>
                          <option value="both">PDF + CSV</option>
                          <option value="pdf">PDF only</option>
                          <option value="csv">CSV only</option>
                        </select>
                        <label className={`flex items-center gap-2 ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
                          <input type="checkbox"
                                 data-testid={`opt-out-${p.code}`}
                                 checked={!off}
                                 disabled={disabled}
                                 onChange={(e) => toggleAutoReports(p.code, !e.target.checked)} />
                          <span className={`text-[10px] tracking-widest2 uppercase ${off ? "text-bronze" : "text-brand-platinum"}`}>
                            {off ? "OFF" : "ON"}
                          </span>
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="border border-brand-border p-5 grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-4 items-end">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">Month</span>
          <input type="month" value={month}
                 onChange={(e) => setMonth(e.target.value)}
                 data-testid="partners-month"
                 className="bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">Partner</span>
          <select value={selected} onChange={(e) => setSelected(e.target.value)}
                  data-testid="partners-select"
                  className="bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-sm">
            {partners.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} — {p.code}
                {p.email ? ` · ${p.email}` : " · (no email)"}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end gap-2">
          <button onClick={preview} disabled={loading || !selected}
                  data-testid="partners-preview"
                  className="btn-pill">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : "Preview"}
          </button>
        </div>
      </div>

      {report && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Trips"    value={report.totals.count} />
            <StatCard label="Gross"    value={`CHF ${report.totals.gross_chf.toFixed(2)}`} />
            <StatCard label="Discount" value={`- CHF ${report.totals.discount_chf.toFixed(2)}`} />
            <StatCard label="Net"      value={`CHF ${report.totals.net_chf.toFixed(2)}`} highlight />
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={openPdf} className="btn-pill" data-testid="partners-open-pdf">
              <FileText size={14} /> Open PDF
            </button>
            <button onClick={downloadCsv} className="btn-pill" data-testid="partners-csv">
              <Download size={14} /> Download CSV
            </button>
            <button onClick={sendOne} disabled={busy || report.totals.count === 0}
                    className="btn-pill btn-pill--solid" data-testid="partners-send-one">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Email statement to partner
            </button>
            <button onClick={sendAll} disabled={busy}
                    className="btn-pill" data-testid="partners-send-all">
              <Send size={14} /> Send to ALL partners for {month}
            </button>
          </div>

          {report.reservations.length === 0 ? (
            <p className="text-sm text-brand-dim italic">
              No paid reservations for {report.code} in {report.month}.
            </p>
          ) : (
            <div className="border border-brand-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-brand-surface">
                  <tr className="text-brand-silver">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Ref</th>
                    <th className="text-left px-3 py-2">Route / Service</th>
                    <th className="text-right px-3 py-2">Gross</th>
                    <th className="text-right px-3 py-2">Discount</th>
                    <th className="text-right px-3 py-2">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {report.reservations.map((r) => (
                    <tr key={r.id} className="border-t border-brand-border text-brand-platinum">
                      <td className="px-3 py-2 tabular-nums">
                        {new Date(r.pickup_datetime).toLocaleString(undefined, {
                          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                        })}
                      </td>
                      <td className="px-3 py-2 font-mono">{r.short_id}</td>
                      <td className="px-3 py-2">{r.origin && r.destination ? `${r.origin} → ${r.destination}` : r.service}</td>
                      <td className="px-3 py-2 text-right tabular-nums">CHF {r.gross_chf.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-bronze">- CHF {r.discount_chf.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">CHF {r.net_chf.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, highlight }) => (
  <div className={`border p-4 ${highlight ? "border-bronze bg-bronze/[0.05]" : "border-brand-border"}`}>
    <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-1">{label}</p>
    <p className={`text-lg tabular-nums ${highlight ? "text-bronze font-semibold" : "text-brand-platinum"}`}>{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Calendar Sync panel — Apple Calendar (iCloud CalDAV) integration.
// Shows connection status, last sync, upcoming events, run logs, plus test
// and resync actions.
// ---------------------------------------------------------------------------

const CalendarSyncPanel = () => {
  const [status, setStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, ev, lg] = await Promise.all([
        adminApi.get("/admin/calendar/status"),
        adminApi.get("/admin/calendar/events?days=60"),
        adminApi.get("/admin/calendar/logs"),
      ]);
      setStatus(s.data);
      setEvents(ev.data.events || []);
      setLogs(lg.data.runs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load calendar status");
    }
  };

  useEffect(() => { load(); }, []);

  const doTest = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data } = await adminApi.post("/admin/calendar/test");
      setTestResult(data);
      if (data.ok) toast.success(`Connected · ${data.calendar}`);
      else toast.error(`Test failed: ${data.error}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
    setBusy(false);
  };

  const doResync = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/calendar/resync");
      if (data.ok) toast.success(`Resynced · ${data.count} events (${data.ours_count} ours · ${data.external_count} manual)`);
      else toast.error(`Resync failed: ${data.error}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resync failed");
    }
    setBusy(false);
  };

  if (!status) return <p className="text-brand-silver text-sm">Loading…</p>;

  return (
    <div data-testid="admin-sync-panel" className="space-y-6">
      <p className="text-sm text-brand-silver max-w-3xl">
        The Apple Calendar (<span className="text-brand-platinum">{status.calendar_name || "—"}</span>) is
        the single source of truth for availability. Every event in it makes
        the slot unavailable for new bookings. New reservations are pushed to
        the calendar automatically; cancellations and completions remove them.
      </p>

      <div className="border border-brand-border p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Connection" value={status.configured ? "Configured" : "NOT configured"} highlight={status.configured} />
          <StatCard label="Provider" value={status.provider || "—"} />
          <StatCard label="Calendar" value={status.calendar_name || "—"} />
          <StatCard label="Last sync" value={status.last_sync?.run_at
            ? new Date(status.last_sync.run_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
            : "never"} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={doTest} disabled={busy} data-testid="cal-test" className="btn-pill">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Test connection
        </button>
        <button onClick={doResync} disabled={busy} data-testid="cal-resync" className="btn-pill btn-pill--solid">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
          Force resync
        </button>
      </div>

      {testResult && (
        <div className={`border p-4 text-sm ${testResult.ok ? "border-brand-platinum text-brand-platinum" : "border-bronze text-bronze"}`}>
          {testResult.ok ? `Connected to ${testResult.calendar}` : testResult.error}
        </div>
      )}

      <div>
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">Upcoming busy intervals (next 60 days)</p>
        {events.length === 0 ? (
          <p className="text-sm text-brand-dim italic">Calendar is empty for the next 60 days.</p>
        ) : (
          <div className="border border-brand-border overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-brand-surface">
                <tr className="text-brand-silver">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Start</th>
                  <th className="text-left px-3 py-2">End</th>
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i} className="border-t border-brand-border text-brand-platinum">
                    <td className="px-3 py-2 tabular-nums">{new Date(e.start_utc).toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}</td>
                    <td className="px-3 py-2 tabular-nums">{new Date(e.start_utc).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-3 py-2 tabular-nums">{new Date(e.end_utc).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-3 py-2">{e.summary}</td>
                    <td className="px-3 py-2 text-[10px] tracking-widest2 uppercase">
                      {e.ours ? <span className="text-bronze">SwissAlpenLine</span> : <span className="text-brand-silver">Manual / iPhone</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">Recent sync attempts</p>
        {logs.length === 0 ? (
          <p className="text-sm text-brand-dim italic">No sync attempts yet.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {logs.slice(0, 10).map((l, i) => (
              <li key={i} className="flex items-center justify-between text-brand-silver">
                <span className="tabular-nums">
                  {new Date(l.run_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {l.auto ? " · auto" : " · manual"}
                </span>
                <span className={l.ok ? "text-brand-platinum" : "text-bronze"}>
                  {l.ok ? `${l.count} events (${l.ours_count} ours · ${l.external_count} manual)` : l.error}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

